/**
 * The parking lot: forty-odd cars in bays, six of them pushed far enough out to
 * make a slalom of it, and the boot lids a full vauhti meter can clear.
 *
 * Every box hashed, boot split and all. This is the longest stretch of the run
 * and the one that decides most of the time.
 */

import { solid, type SolidSpec } from '../world/Level';
import { clamp } from '../utils/math';
import { mulberry32 } from '../utils/prng';

/* ------------------------------------------------------- parkkipaikka --- */

/**
 * The lot. "Alueella on paljon tilaa autoille" - so there is, and every one of
 * them is something to run into.
 *
 * The aisle is one long diagonal rather than an axis-aligned lane: it has to
 * carry the runner from the pirtti's door down towards the navetta, and a
 * straight lane followed by a corner would be a place to stop rather than a
 * place to carry speed through. Bays are nose-in down both sides, and six cars
 * have been left sticking out into the lane by people who arrived at four in
 * the morning. Those four are the slalom.
 */
const PARKING = {
  start: 37.0,
  end: 92.0,
  startZ: 0.6,
  endZ: -13.0,
  /** Half the clear width of the aisle. 2.7 m each way = a 5.4 m lane. */
  halfWidth: 2.7,
} as const;

/** Centre line of the aisle at a given x. Clamped, so the ends stay straight. */
function aisleZ(x: number): number {
  const t = clamp((x - PARKING.start) / (PARKING.end - PARKING.start), 0, 1);
  return PARKING.startZ + t * (PARKING.endZ - PARKING.startZ);
}

/**
 * ==========================================================================
 *  A CAR, AND THE ONE PART OF ONE YOU MAY JUMP
 * ==========================================================================
 *  `bodyHeight` is the whole car - roof included, not just the waistline - and
 *  it is 1.72 m for a reason that has nothing to do with what a car looks
 *  like. The vauhti meter pays for lift as well as for speed
 *  (`MOMENTUM.jumpBoost`), so the apex runs from 0.71 m cold to 0.93 m flat
 *  out, and *anything* a player can get on top of in this lot is a road: the
 *  bays are 2.4 m apart and a car is 1.85 m wide, so a 0.55 m gap between two
 *  roofs is one a 0.68 m player walks straight over. A reachable roof would
 *  mean hopping one boot lid, stepping up onto its own car, and walking the
 *  left-hand bay row out into the neighbour's field - which is the exact
 *  sixty-metre skip `AITAUS` was built to close.
 *
 *  So the roofline is put out of reach twice over: out of reach from the
 *  ground (0.93 m apex), and out of reach from `bootHeight` as well
 *  (0.73 + 0.93 = 1.66 m, under the 1.72 m roof).
 *
 *  That second figure used to carry a cushion and no longer does. It read
 *  "and a runner who has just landed on a boot has spent a jump's worth of
 *  meter getting there, so the real figure is lower still" - which was true
 *  while a jump onto a lid was charged for. It is not: landing on a lid hands
 *  the take-off price straight back (`MOMENTUM.jumpFactor`), so a runner
 *  standing on a boot has whatever he arrived with, and 1.66 m is now the
 *  honest number rather than a conservative one. The 60 mm under the roof is
 *  the whole of the margin, and `Level.test.ts` asserts it at `MOMENTUM.max`
 *  rather than at anything the route produces, precisely because it is.
 *
 *  What is left inside the apex is `bootHeight`, and only on the cars that are
 *  reversed into their bays - see `BADLY_PARKED`. Those are the ones sticking
 *  out into the lane, so the one part of a car you can vault is the one part
 *  that is in your way, and you can see which cars they are from fifty metres
 *  because their boots are pointing at you.
 *
 *  All three of these numbers are priced by running at the thing rather than
 *  by comparing two others, because a hurdle you clear exactly at your apex is
 *  a hurdle with no take-off point. `Level.test.ts` drives the sweep; the lid
 *  currently costs a third of a meter, which is about four seconds of clean
 *  running, and the route can afford that with room to spare.
 * ==========================================================================
 */
export const CAR = {
  length: 4.4,
  width: 1.85,
  /**
   * The collider, and the drawn silhouette with it. Above every apex the meter
   * can buy, from the ground and from a boot lid alike.
   */
  bodyHeight: 1.72,
  /**
   * The boot lid of a car reversed in. Above the 0.71 m cold apex, below the
   * 0.93 m one a full meter buys: the tail is a wall at a walk and a hurdle at
   * thirty. `Level.test.ts` pins both halves of that by running at it.
   *
   * It has come down twice, from 0.80 and then from 0.76, and both times for
   * the same reason: the lid was priced where nobody was buying. At 0.76 the
   * sweep put it at half the bar, which is six seconds of clean running in a
   * lane where any contact drops the meter back to `min` - so the shortcut was
   * reachable on paper and still did not get taken. At 0.73 the first take-off
   * point opens at a third of the bar and there are three of them by 1.40,
   * where the 0.76 lid offered one at 1.44 and did not widen to three until
   * 1.52. The window is the part that reads as fair; the threshold on its own
   * only says the trick exists.
   *
   * The price has come down once more since, to 0.31 of the bar, and that one
   * was not this number: `MOMENTUM.jumpBoost` went 0.14 -> 0.16. Which is the
   * note below, arriving in practice.
   *
   * This is as low as it goes. What is left above the cold apex is 17 mm, and
   * that gap is the whole of the "wall at a walk" half - a lid you can hop
   * from a standing start is not a hurdle at all. Cheaper than this means
   * `MOMENTUM.jumpBoost`, which is the same lever from the other end (0.16
   * against a 0.74 m lid measures identically to 0.14 against this one) but
   * raises every jump in the game rather than this one.
   */
  bootHeight: 0.73,
  /**
   * How much of the tail is lid rather than cabin.
   *
   * Longer than a tailgate needs to be, and the reason is the last two badly
   * parked cars. They sit on opposite sides of an aisle that runs diagonally,
   * so their lids are offset across the lane as well as along it; a short lid
   * leaves the two barely overlapping, and a hop from one to the other lands
   * in the far car's cabin, which is a wall. The lid is what buys the overlap,
   * so this is what makes the pair a pair rather than two separate hurdles.
   * It changes no gap in the lane - `jut` sets those - so the slalom for
   * anybody who stays on the ground is exactly what it was.
   */
  bootLength: 1.6,
} as const;

/** The letters a registration is drawn from. */
const PLATE_LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

/**
 * The registration on a given vehicle.
 *
 * Every car and every matkailuauto in the level wears one. A yard where two
 * cars are lettered and forty are blank reads as two props standing in a lot
 * of scenery, and the lot is the longest stretch of the route - it is looked
 * at for longer than anything else in the game. The two in the ansalanka row
 * keep the erikoistunnukset they were given by hand; everything else gets a
 * plain `ABC-123`, three letters and a number between 100 and 999, which is
 * what a Finnish plate is when nobody has paid for it to say anything.
 *
 * Derived from the vehicle's *own name* rather than dealt out by a running
 * generator, and that is the point: the plate belongs to that car and not to
 * the position it happens to hold in an array, so parking another one halfway
 * down the row does not re-letter every car behind it. Same rule as the
 * klapiteline's stack (`src/utils/prng.ts`, fixed seed, module load), so every
 * machine and every replay sees the same yard - see README, *Determinismi*.
 *
 * Render-only, like the shape and the badge: `simulationFingerprint()` hashes
 * a solid's colliders, and lettering a bumper is not one of them.
 */
export function plateFor(name: string): string {
  // FNV-1a over the name, to turn it into a seed. Any hash would do; this one
  // is three lines and separates names that differ in one character, which is
  // most of what it is handed - `auto-p7` against `auto-p8`.
  let hash = 0x811c9dc5;
  for (let at = 0; at < name.length; at++) {
    hash = Math.imul(hash ^ name.charCodeAt(at), 0x01000193);
  }
  const rng = mulberry32(hash);

  let letters = '';
  for (let at = 0; at < 3; at++) {
    letters += PLATE_LETTERS[Math.floor(rng() * PLATE_LETTERS.length)];
  }
  // 100-999: the number half of a Finnish plate has no leading zero, so the
  // first digit is 1-9 and the other two are anything. `Level.test.ts` holds
  // every plate in the yard to that shape, and to being nobody else's.
  return `${letters}-${100 + Math.floor(rng() * 900)}`;
}

const BAY_FIRST = 38.6;
const BAY_SPACING = 2.4;
const BAY_COUNT = 21;

/**
 * Six unequal bends. The last pair is only 4.8 m apart, where momentum is
 * highest: each gap needs its own approach instead of repeating one rhythm.
 * Clear widths are 2.25–2.55 m; the reversed cars also offer earned boot vaults.
 */
const BADLY_PARKED: { bay: number; side: -1 | 1; jut: number }[] = [
  { bay: 3, side: -1, jut: 2.9 },
  { bay: 7, side: 1, jut: 3.05 },
  { bay: 12, side: -1, jut: 3.15 },
  { bay: 15, side: 1, jut: 2.95 },
  { bay: 18, side: -1, jut: 3.1 },
  { bay: 20, side: 1, jut: 2.85 },
];

function parkedCar(name: string, x: number, side: -1 | 1, jut: number, alt: boolean): SolidSpec {
  // `aisle` is the end nearest the lane, whichever way round the car is parked.
  const aisle = aisleZ(x) + side * (PARKING.halfWidth - jut);
  const far = aisle + side * CAR.length;
  const halfW = CAR.width / 2;
  return {
    ...solid(
      name,
      alt ? 'carAlt' : 'car',
      x - halfW,
      Math.min(aisle, far),
      x + halfW,
      Math.max(aisle, far),
      CAR.bodyHeight,
    ),
    // Wheels, glass and lights are World's problem; the box is the whole of the
    // gameplay. See SolidShape.
    shape: 'car',
    plate: plateFor(name),
    // Reversed in, so the low end is the end in the lane. Only the badly
    // parked ones get one: a boot lid nobody can reach is not a shortcut, and
    // a boot lid on a car in the boundary row would be a way over the fence.
    boot:
      jut > 0
        ? {
            length: CAR.bootLength,
            height: CAR.bootHeight,
            // side -1 parks the car towards -z, so its lane end is maxZ.
            end: side < 0 ? 1 : -1,
          }
        : undefined,
  };
}

function buildParking(): SolidSpec[] {
  const cars: SolidSpec[] = [];
  for (let bay = 0; bay < BAY_COUNT; bay++) {
    const x = BAY_FIRST + bay * BAY_SPACING;
    for (const side of [-1, 1] as const) {
      // The first south-side bay is occupied by the spruce at the pirtti exit.
      if (bay === 0 && side === 1) continue;
      const bad = BADLY_PARKED.find((entry) => entry.bay === bay && entry.side === side);
      const name = `auto-${side < 0 ? 'p' : 'e'}${bay + 1}`;
      cars.push(parkedCar(name, x, side, bad?.jut ?? 0, (bay + (side < 0 ? 0 : 1)) % 2 === 0));
    }
  }
  return cars;
}

export const PARKED = buildParking();

/** "Voit tarvittaessa vuokrata matkailuautopaikan pihapiiristä." */
export function camper(name: string, minX: number, minZ: number): SolidSpec {
  return {
    ...solid(name, 'carAlt', minX, minZ, minX + 6.6, minZ + 2.4, 2.15),
    shape: 'camper',
    // Registered like anything else on the road, and parked where they are
    // walked past rather than driven past. See `plateFor`.
    plate: plateFor(name),
  };
}

// Pitched on the grass behind the pirtti rather than along the side of the
// lot, because the side of the lot is the neighbour's field now - see
// `AITAUS`. The back field is where a venue would put them anyway: off the
// gravel, and far enough out that the two lines out of the pirtti still have
// the whole takapiha to bend through.
export const CAMPERS = [camper('matkailuauto-1', 19.6, -16.2), camper('matkailuauto-2', 28.0, -16.6)];
